import { ReactElement, ReactNode, useEffect, useRef, useState } from "react";

import {
    ALL_VALUE,
    AllValueType,
    EMPTY_STRING,
    INTEGER_ONLY_REGEX,
    PERCENT_DECIMALS,
    PERCENT_ONLY_REGEX,
    colorToAlpha,
    doNothing
} from "@bridgesplit/utils";
import { Check, Close, ExpandMore, Remove, Search } from "@mui/icons-material";
import {
    MenuItem,
    Select as MuiSelect,
    StandardTextFieldProps,
    TextField,
    SelectProps as MuiSelectProps,
    styled,
    Checkbox as MuiCheckbox,
    Switch as MuiSwitch,
    CheckboxProps,
    FormControl,
    Slider as MuiSlider,
    SliderProps as MuiSliderProps,
    SwitchProps,
    StackProps,
    InputAdornment,
    TextFieldProps,
    Autocomplete as MuiAutocomplete,
    Box,
    SxProps,
    Theme,
    MenuItemProps
} from "@mui/material";
import { NumericFormat, PatternFormat } from "react-number-format";
import dayjs, { Dayjs } from "dayjs";
import {
    DateTimePicker as MuiDateTimePicker,
    DateTimePickerProps as MuiDateTimePickerProps
} from "@mui/x-date-pickers/DateTimePicker";

import { Column, Row } from "./containers";
import { Text, TextVariant, TooltipText, TextSkeleton, TooltipTextProps, TextColor } from "./typography";
import { SxType } from "../types";
import {
    BORDER_RADIUS,
    BORDER_RADIUS_ROUNDED,
    FONT_SIZES,
    INPUT_HEIGHT,
    LIGHT_SHADOW,
    SPACING,
    SPACING_PX,
    useAppPalette,
    useTextColorMap
} from "../theme";
import { useOutsideAlerter } from "../util";
import { Icon } from "./icons";
import { Button, ButtonBase, TextButton } from "./buttons";

export type SelectOption<T> = {
    label?: string | ReactElement;
    value: NonNullable<T>;
    disabled?: boolean;
    key?: string | number;
};

const Input = styled(TextField)(({ theme }) => ({
    "& .MuiOutlinedInput-root": {
        p: 1,
        height: INPUT_HEIGHT + "px",
        border: 0,
        transition: `border background 0.2s ease-out`,
        "& fieldset": {
            borderColor: theme.palette.divider
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.secondary,
            borderWidth: "1px"
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.text.disabled
        },
        borderRadius: 0
    }
}));

export interface SelectProps<T extends string | number> {
    value: T | undefined;
    setValue: (val: T) => void;
    options: SelectOption<T>[];
    renderValue?: (val: T | AllValueType) => ReactNode;
    hideIcon?: boolean;
    sx?: SxType;
    maxMenuHeight?: number;
    loading?: boolean;
    disabled?: boolean;
    selectProps?: Pick<MuiSelectProps<T>, "variant" | "disableUnderline" | "startAdornment">;
    menuItemProps?: Pick<MenuItemProps, "sx">;
}
export function Select<T extends string | number>({
    value,
    setValue,
    renderValue,
    hideIcon,
    options,
    maxMenuHeight,
    sx,
    loading,
    disabled,
    selectProps,
    menuItemProps
}: SelectProps<T>) {
    return (
        <StyledSelect
            MenuProps={{ PaperProps: { sx: { maxHeight: maxMenuHeight } } }}
            IconComponent={hideIcon ? () => null : undefined}
            renderValue={loading ? () => <TextSkeleton variant="h4" /> : renderValue}
            inputProps={{
                sx: { pr: hideIcon ? `${SPACING_PX} !important` : `${SPACING + FONT_SIZES.body1}px !important` }
            }}
            disabled={loading || disabled}
            fullWidth
            value={value ?? ALL_VALUE}
            onChange={(val) => setValue(val.target.value as T)}
            sx={sx}
            {...selectProps}
        >
            {options.map((o, i) => (
                <MenuItem
                    disabled={o.disabled}
                    value={o.value}
                    key={o.key ?? (typeof o.value === "string" ? o.value : o.value.toString() + i)}
                    sx={{ minHeight: "0px", borderRadius: BORDER_RADIUS, p: 1, ...menuItemProps?.sx }}
                >
                    {o.label ?? o.value}
                </MenuItem>
            ))}
        </StyledSelect>
    );
}

export function InvisibleSelect<T extends string | number>({
    sx,
    selectProps,
    variant = "body1",
    color,
    showBorder = false,
    ...props
}: SelectProps<T> & { variant?: TextVariant; color?: TextColor; showBorder?: boolean }) {
    const colorHex = useTextColorMap()(color ?? "body");
    return (
        <Select
            selectProps={{ disableUnderline: true, variant: showBorder ? "outlined" : "standard", ...selectProps }}
            {...props}
            sx={{
                borderRadius: BORDER_RADIUS,
                ".MuiInputBase-input": {
                    px: showBorder ? 1.5 : 0,
                    ":focus": { background: "none" }
                },
                height: FONT_SIZES.body1 + (showBorder ? 8 : 0) + "px",
                color: colorHex,
                svg: { fontSize: FONT_SIZES.body1 },
                width: "max-content",
                fontSize: FONT_SIZES[variant],
                ...sx
            }}
            menuItemProps={{
                sx: {
                    fontSize: FONT_SIZES[variant],
                    py: variant === "body2" || variant === "caption" ? 0.5 : undefined
                }
            }}
        />
    );
}

type OutlinedOptionProps = {
    onClick?: () => void;
    isSelected: boolean;
    disabled?: boolean;
    children: ReactNode;
    padding?: number;
    sx?: SxType;
    color?: "primary" | "secondary";
};
export function OutlinedOption({
    onClick,
    isSelected,
    disabled,
    children,
    padding = 2,
    sx,
    color = "primary"
}: OutlinedOptionProps) {
    const { border, secondary, hoverOpacity, primary } = useAppPalette();

    const base = color === "secondary" ? secondary : primary;

    return (
        <Column
            sx={{
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? "not-allowed" : "pointer",
                ":hover": { background: disabled ? undefined : colorToAlpha(base, hoverOpacity * 1.5) },
                p: padding,
                background: disabled || !isSelected ? undefined : colorToAlpha(base, hoverOpacity),
                borderRadius: BORDER_RADIUS,
                outline: isSelected ? `1.5px solid ${base}` : border,
                ...sx
            }}
            onClick={disabled ? doNothing : onClick}
        >
            {children}
        </Column>
    );
}

export function OutlinedChip({ sx, children, isSelected, ...props }: OutlinedOptionProps) {
    const { secondary, hoverOpacity, primary } = useAppPalette();

    const base = props.color === "secondary" ? secondary : primary;
    const active = !props.disabled && !!isSelected;
    return (
        <Button
            height={30}
            textProps={{ sx: { color: colorToAlpha(base, active ? 1 : 0.6) } }}
            variant="outlined"
            sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: BORDER_RADIUS_ROUNDED,
                minWidth: "max-content",
                borderColor: active ? base : undefined,
                background: active ? colorToAlpha(base, hoverOpacity) : undefined,
                ...sx
            }}
            {...props}
        >
            {children}
        </Button>
    );
}

function StyledSelect<T>({ sx, inputProps, ...otherProps }: MuiSelectProps<T | AllValueType>) {
    const { inputBorder, textDisabled, isLightMode, hoverBackground, background, cardBackground } = useAppPalette();

    return (
        <MuiSelect
            displayEmpty
            MenuProps={{
                elevation: 0,
                disableScrollLock: true,
                sx: {
                    marginTop: 0.5
                },
                slotProps: {
                    paper: {
                        sx: {
                            backgroundColor: background,
                            backgroundImage: "none",
                            boxShadow: isLightMode ? LIGHT_SHADOW : undefined,
                            border: `1px ${inputBorder} solid`,
                            borderRadius: BORDER_RADIUS
                        }
                    },
                    list: {
                        sx: {
                            padding: 0.5,
                            display: "flex",
                            flexDirection: "column",
                            backgroundColor: cardBackground,
                            ".Mui-selected": {
                                backgroundColor: hoverBackground
                            },
                            rowGap: 0.5
                        }
                    }
                }
            }}
            inputProps={{
                ...inputProps
            }}
            sx={{
                ".MuiOutlinedInput-notchedOutline": {
                    borderColor: inputBorder
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: inputBorder,
                    borderWidth: "1px"
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: textDisabled
                },
                "& .MuiSelect-select": {
                    p: 1,
                    display: "flex"
                },
                svg: { fontSize: "inherit" },
                ".MuiPaper-root": { boxShadow: "none" },
                borderRadius: 0,
                ...sx
            }}
            {...otherProps}
            IconComponent={otherProps.IconComponent ?? ExpandMore}
        />
    );
}

export function SearchInput({
    forceFocus,
    hideBorder,
    size = "medium",
    sx,
    ...props
}: TextFieldProps & { forceFocus?: boolean; hideBorder?: boolean }) {
    const inputRef = useRef<HTMLInputElement>(null);
    const smallSearchSx = {
        ".MuiInputBase-input": {
            fontSize: FONT_SIZES.body2
        },
        "& .MuiOutlinedInput-root": {
            height: "35px"
        }
    };
    useEffect(() => {
        if (!inputRef.current || !forceFocus) return;
        inputRef.current.focus();
    }, [forceFocus]);

    return (
        <Input
            sx={{
                ...(hideBorder && { "& fieldset": { border: "none" } }),
                ...(size === "small" && smallSearchSx),
                ...sx
            }}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <Search fontSize="inherit" color="disabled" />
                    </InputAdornment>
                ),
                inputRef
            }}
            {...props}
        />
    );
}

interface FormInputProps
    extends Omit<StandardTextFieldProps, "label" | "variant" | "margin" | "defaultValue" | "type"> {
    adornment?: ReactNode;
    label?: string;
    tooltip?: string;
    errorMessage?: string;
    alwaysShowError?: boolean;
    multiLineHeight?: number;
    unitSelect?: SelectProps<string | number>;
    variant?: "number" | "string";
    isControlled?: boolean;
    type?: "text" | "password" | "tel";
    inputHeight?: number;
    belowInputElement?: ReactNode; // pass in as props to allow elements in between error message
}

interface FormLabelProps {
    label: ReactNode;
    tooltip?: string;
    disabled?: boolean;
    color?: TextColor;
}
export function FormLabel({ label, tooltip, disabled, color = "caption" }: FormLabelProps) {
    return (
        <TooltipText helpText={tooltip ?? ""} variant="body2" color={disabled ? "disabled" : color} sx={{ mb: 0.5 }}>
            {label}
        </TooltipText>
    );
}

export function InputErrorMessage({
    errorMessage,
    showError
}: {
    errorMessage: string | undefined;
    showError: boolean;
}) {
    const errorVisible = showError && !!errorMessage;
    return (
        <Text
            variant="body2"
            color="error"
            sx={{
                mt: errorVisible ? 0.5 : 0,
                opacity: errorVisible ? 1 : 0,
                transition: "opacity 0.3s",
                height: errorVisible ? undefined : "0px"
            }}
        >
            {errorMessage}
        </Text>
    );
}

export function ErrorMessage({ errorMessage }: { errorMessage: string | undefined }) {
    if (!errorMessage) return null;
    return (
        <Text variant="body2" color="error">
            {errorMessage}
        </Text>
    );
}

interface LabelWrapperProps extends FormLabelProps {
    children: ReactNode;
    labelColor?: TextColor;
}
export function LabelWrapper({ children, labelColor, ...props }: LabelWrapperProps) {
    return (
        <Column>
            <FormLabel color={labelColor} {...props} />
            {children}
        </Column>
    );
}

function BaseFormInput({
    value,
    adornment,
    label,
    InputProps,
    tooltip,
    unitSelect,
    errorMessage,
    alwaysShowError,
    multiLineHeight,
    isControlled = true,
    belowInputElement,
    variant,
    inputHeight,
    ...props
}: FormInputProps) {
    const inputProps = props as StandardTextFieldProps;
    delete inputProps.variant;

    return (
        <Input
            value={isControlled ? value ?? "" : undefined}
            {...inputProps}
            multiline={!!multiLineHeight}
            inputProps={{ inputMode: variant === "number" ? "decimal" : undefined, ...props.inputProps }}
            sx={{
                "& .MuiOutlinedInput-root": {
                    height: (() => {
                        if (inputHeight) return `${inputHeight}px`;
                        if (multiLineHeight) return "auto";
                        return undefined;
                    })(),
                    minHeight: multiLineHeight,
                    alignItems: multiLineHeight ? "flex-start" : undefined
                },
                ...inputProps.sx
            }}
            InputProps={{
                endAdornment: (() => {
                    if (unitSelect) {
                        const { sx, ...unitSelectProps } = unitSelect;
                        return (
                            <FormControl>
                                <Select
                                    sx={{
                                        svg: {
                                            fontSize: "1.5rem",
                                            color: "text.secondary"
                                        },
                                        "& fieldset": { border: "none" },
                                        width: "max-content",

                                        pr: 0.5,
                                        border: "none",
                                        ...sx
                                    }}
                                    {...unitSelectProps}
                                />
                            </FormControl>
                        );
                    }

                    if (typeof adornment === "string") {
                        return <Text sx={{ minWidth: "fit-content" }}> {adornment} </Text>;
                    }
                    return adornment;
                })(),
                ...InputProps
            }}
        />
    );
}

function CombinedFormInput(props: FormStringInputProps | FormNumberInputProps) {
    if (props.variant === "number") return <FormNumberInput {...(props as FormNumberInputProps)} />;
    return <FormStringInput {...(props as FormStringInputProps)} />;
}

export function FormInput({
    alwaysShowError,
    label,
    tooltip,
    belowInputElement,
    errorMessage,
    ...props
}: FormStringInputProps | FormNumberInputProps) {
    const [showErrorState, setShowError] = useState(false);
    useOutsideAlerter(() => setShowError(true));
    const showError = (errorMessage && alwaysShowError) || showErrorState;
    return (
        <Column onBlur={() => setShowError(true)} sx={{ width: props.fullWidth ? "100%" : undefined }}>
            {!!label && <FormLabel disabled={props.disabled} label={label} tooltip={tooltip} />}

            <CombinedFormInput {...props} />

            {belowInputElement}
            <InputErrorMessage errorMessage={errorMessage} showError={showError} />
        </Column>
    );
}
/** Shared props for string-based inputs that support U64-range values (no number conversion) */
export interface StringFormInputBaseProps
    extends Omit<FormInputProps, "value" | "setValue" | "variant" | "type" | "label" | "tooltip"> {
    value: string | null | undefined;
    setValue: (v: string | null | undefined) => void;
    /** When true, input is disabled and shows "Disabled" placeholder. Typically when value === null. */
    disabled?: boolean;
    label?: ReactNode;
    tooltip?: string | ReactNode;
    belowInputElement?: ReactNode;
    errorMessage?: string;
    alwaysShowError?: boolean;
}

/** Internal base component for string inputs with validation. Supports U64 max via string values. */
function StringFormInputBase({
    value,
    setValue,
    isAllowed,
    disabled = false,
    label,
    tooltip,
    belowInputElement,
    errorMessage,
    alwaysShowError,
    placeholder,
    fullWidth,
    ...rest
}: StringFormInputBaseProps & {
    isAllowed?: (str: string) => boolean;
}) {
    const [showErrorState, setShowError] = useState(false);
    useOutsideAlerter(() => setShowError(true));
    const showError = (errorMessage && alwaysShowError) || showErrorState;
    const isDisabled = disabled || value === null;
    /** Empty string is treated as DISABLED_APY; show "Disabled" placeholder. */
    const isEmptyValue = value == null || (typeof value === "string" && value.trim() === EMPTY_STRING);
    const effectivePlaceholder = isDisabled || isEmptyValue ? "Disabled" : placeholder;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isDisabled) return;
        const raw = e.target.value;
        const normalized = raw === "" ? "" : raw;
        if (isAllowed && normalized !== "" && !isAllowed(normalized)) return;
        setValue(normalized);
    };

    return (
        <Column onBlur={() => setShowError(true)} sx={{ width: fullWidth ? "100%" : undefined }}>
            {!!label && (
                <FormLabel
                    disabled={isDisabled}
                    label={label}
                    tooltip={typeof tooltip === "string" ? tooltip : undefined}
                />
            )}
            <BaseFormInput
                {...rest}
                variant="string"
                value={value ?? ""}
                onChange={handleChange}
                disabled={isDisabled}
                placeholder={effectivePlaceholder}
                fullWidth={fullWidth}
                inputProps={{
                    ...rest.inputProps,
                    inputMode: isAllowed ? "decimal" : rest.inputProps?.inputMode
                }}
            />
            {belowInputElement}
            <InputErrorMessage errorMessage={errorMessage} showError={showError} />
        </Column>
    );
}

/** String input for percent values (digits, optional decimal). Supports full precision for BPS/U64. */
export function StringPercentInput({
    adornment = "%",
    ...props
}: StringFormInputBaseProps & { adornment?: ReactNode }) {
    return <StringFormInputBase {...props} isAllowed={(str) => PERCENT_ONLY_REGEX.test(str)} adornment={adornment} />;
}

/** String input for integer values. Supports values up to U64 max (no number conversion). */
export function StringBigIntInput(props: StringFormInputBaseProps) {
    return <StringFormInputBase {...props} isAllowed={(str) => INTEGER_ONLY_REGEX.test(str)} />;
}

export interface FormStringInputProps extends FormInputProps {
    value: string | undefined;
    setValue: (n: string | undefined) => void;
    variant: "string";
    pattern?: { format: string; mask?: string | string[] };
}

function FormStringInput({ setValue, type, value, pattern, ...props }: FormStringInputProps) {
    if (!pattern) {
        return <BaseFormInput {...props} type={type} value={value} onChange={(e) => setValue(e.target.value)} />;
    }
    return (
        <PatternFormat
            format={pattern.format}
            mask={pattern.mask}
            {...props}
            type={type}
            customInput={BaseFormInput}
            value={value}
            onChange={(e) => setValue(e.target.value)}
        />
    );
}

export interface FormNumberInputProps extends FormInputProps {
    value: number | undefined;
    setValue: (n: number | undefined) => void;
    decimals?: number;
    variant: "number";
    thousandSeparator?: boolean;
    allowNegative?: boolean;
    validateNumber?: (n: number | undefined) => boolean;
}

function FormNumberInput({
    value,
    setValue,
    decimals,
    thousandSeparator = true,
    allowNegative = false,
    validateNumber,
    ...props
}: FormNumberInputProps) {
    // `react-number-format` gives us both a numeric value and the user's raw string.
    // We preserve the raw string for "in-progress" states that shouldn't be reformatted
    // (e.g. ".", "0.", "1.", "1.2300"). This prevents caret jumps and preserves intent
    // while still emitting a usable numeric value upstream.
    const rawDisplayRef = useRef<string | null>(null);

    let displayValue: string;

    if (value === undefined) {
        displayValue = "";
        rawDisplayRef.current = null;
    } else if (rawDisplayRef.current != null) {
        const raw = rawDisplayRef.current;
        const parsed = Number(raw);

        // Special-case "zero with formatting": strings like "." / "0." / ".00" / "0.000"
        // don't round-trip cleanly through Number(), but they *do* correspond to `value === 0`
        // while the user is still typing.
        const isZeroWithFormatting = value === 0 && (raw === "." || raw === "0." || /^0?\.0*$/.test(raw));

        // Use an epsilon: `value` may have been produced via parsing/rounding; we only
        // abandon the raw display when it clearly diverges from the numeric state.
        const matches = isZeroWithFormatting || (Number.isFinite(parsed) && Math.abs(parsed - value) < 1e-12);

        if (matches) {
            displayValue = raw;
        } else {
            // If the preserved raw string no longer matches `value`, drop it and fall back
            // to a canonical string form derived from the numeric value.
            rawDisplayRef.current = null;
            displayValue = String(value);
        }
    } else {
        displayValue = String(value);
    }

    return (
        <NumericFormat
            isAllowed={validateNumber ? (val) => validateNumber(val.floatValue) : undefined}
            thousandSeparator={thousandSeparator ? "," : undefined}
            {...props}
            customInput={BaseFormInput}
            allowNegative={allowNegative}
            value={displayValue}
            valueIsNumericString
            decimalScale={decimals}
            onValueChange={(e) => {
                const raw = e.value;

                // Allow a transient "-" while the user is mid-entry.
                // In this state `floatValue` is undefined; we don't want to clobber input.
                if (raw.startsWith("-") && e.floatValue === undefined) return;

                // Cleared input should propagate `undefined` (not 0) and reset raw state.
                if (raw === "") {
                    rawDisplayRef.current = null;
                    setValue(undefined);
                    return;
                }

                const hasDecimal = raw.includes(".");
                const preserveRaw =
                    raw === "." ||
                    raw === "0." || // preserve a trailing decimal while typing
                    raw.startsWith(".") || // allow shorthand like ".1" / ".0"
                    raw.endsWith(".") || // preserve "1." while typing fractional part
                    (hasDecimal && raw.endsWith("0")); // preserve explicit precision like "1.2300"

                if (preserveRaw) {
                    rawDisplayRef.current = raw;
                    // Even when preserving the raw string for display, always emit a numeric
                    // value for consumers. For raw-only states like ".", treat as 0 while typing.
                    setValue(e.floatValue ?? 0);
                    return;
                }

                rawDisplayRef.current = null;
                setValue(e.floatValue);
            }}
        />
    );
}

export function PercentInput({ placeholder, ...props }: Omit<FormNumberInputProps, "variant" | "decimals">) {
    return (
        <FormInput decimals={PERCENT_DECIMALS} adornment="%" variant="number" placeholder={placeholder} {...props} />
    );
}

export interface CustomDateTimePickerProps extends MuiDateTimePickerProps {
    label?: string;
    disabled?: boolean;
    tooltip?: React.ReactNode;
    errorMessage?: string;
    alwaysShowError?: boolean;
    inputHeight?: number;
    sx?: SxProps<Theme>;
    InputProps?: object;
    value: Dayjs | null;
    onChange: (value: Dayjs | null) => void;
}

export function DateTimePicker({
    value,
    onChange,
    label,
    disabled = false,
    tooltip,
    errorMessage,
    alwaysShowError,
    inputHeight,
    sx,
    InputProps,
    ...otherProps
}: CustomDateTimePickerProps) {
    return (
        <>
            <MuiDateTimePicker
                referenceDate={dayjs()}
                value={value}
                onChange={onChange}
                label={label}
                disabled={disabled}
                slotProps={{
                    openPickerIcon: {
                        component: ExpandMore,
                        sx: { color: "text.secondary" }
                    },
                    openPickerButton: {
                        sx: { color: "text.secondary" }
                    },
                    textField: {
                        fullWidth: true,
                        variant: "outlined",
                        error: !!errorMessage,
                        helperText: errorMessage,
                        InputProps: {
                            ...InputProps
                        },
                        sx: {
                            "& .MuiOutlinedInput-root": {
                                height: inputHeight ? `${inputHeight}px` : "40px",
                                alignItems: "center",
                                borderRadius: "8px",
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderWidth: "0.5px"
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderWidth: "0.5px"
                                },
                                "& .MuiIconButton-root": {
                                    backgroundColor: "transparent"
                                }
                            },
                            ...sx
                        }
                    }
                }}
                {...otherProps}
            />
            {tooltip}
        </>
    );
}

const CHECKBOX_SIZE = 12;
const CHECKBOX_RADIUS = "3px";
const CHECKBOX_ICON_SIZE = 10;

function CheckboxIconBase({ filled }: { filled: boolean }) {
    const { inputBorder, secondary } = useAppPalette();
    return (
        <Box
            sx={{
                width: CHECKBOX_SIZE,
                height: CHECKBOX_SIZE,
                borderRadius: CHECKBOX_RADIUS,
                backgroundColor: filled ? secondary : "transparent",
                border: filled ? "none" : `1px solid ${inputBorder}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
            }}
        />
    );
}

function CheckboxCheckedIcon({ icon }: { icon: "check" | "dash" }) {
    return (
        <Box sx={{ position: "relative", display: "inline-flex" }}>
            <CheckboxIconBase filled />
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "common.white",
                    pointerEvents: "none"
                }}
            >
                {icon === "check" ? (
                    <Check sx={{ fontSize: CHECKBOX_ICON_SIZE }} />
                ) : (
                    <Remove sx={{ fontSize: CHECKBOX_ICON_SIZE }} />
                )}
            </Box>
        </Box>
    );
}

export function Checkbox({ sx, ...props }: Omit<CheckboxProps, "disableRipple" | "size">) {
    return (
        <MuiCheckbox
            size="small"
            disableRipple
            icon={<CheckboxIconBase filled={false} />}
            checkedIcon={<CheckboxCheckedIcon icon="check" />}
            indeterminateIcon={<CheckboxCheckedIcon icon="dash" />}
            sx={{
                p: 0,
                width: "fit-content",
                ...sx
            }}
            {...props}
        />
    );
}

export function Switch({ sx, ...props }: Omit<SwitchProps, "disableRipple">) {
    return <MuiSwitch disableRipple sx={{ m: -1, ...sx }} {...props} />;
}

export function CheckboxRow({
    value,
    setValue,
    label,
    textProps,
    spaceBetween
}: {
    value: boolean;
    setValue: (t: boolean) => void;
    label: string;
    textProps?: Omit<TooltipTextProps, "children">;
    spaceBetween?: boolean;
}) {
    return (
        <Row
            spaceBetween={spaceBetween}
            onClick={() => (value ? setValue(false) : setValue(true))}
            sx={{ width: "100%", cursor: "pointer", flexDirection: spaceBetween ? "row-reverse" : undefined }}
            spacing={1}
        >
            <Checkbox checked={!!value} />

            <TooltipText color={textProps?.color ?? "caption"} {...textProps}>
                {label}
            </TooltipText>
        </Row>
    );
}

export function SwitchRow({
    value,
    setValue,
    label,
    textProps,
    switchColor = "secondary",
    disabled,
    spaceBetween = true
}: {
    value: boolean;
    setValue: (t: boolean) => void;
    label: string | ReactElement;
    textProps?: Omit<TooltipTextProps, "children">;
    switchColor?: "primary" | "secondary";
    disabled?: boolean;
    spaceBetween?: boolean;
}) {
    return (
        <Row
            onClick={disabled ? undefined : () => (value ? setValue(false) : setValue(true))}
            sx={{ width: spaceBetween ? "100%" : undefined, cursor: disabled ? "not-allowed" : "pointer" }}
            spaceBetween={spaceBetween}
            spacing={1}
        >
            {typeof label === "string" ? (
                <TooltipText color={disabled ? "disabled" : textProps?.color ?? "caption"} {...textProps}>
                    {label}
                </TooltipText>
            ) : (
                label
            )}
            <Switch disabled={disabled} checked={!!value} color={switchColor} />
        </Row>
    );
}

const SliderBase = styled(MuiSlider)(() => ({
    "& .MuiSlider-track": {
        border: "none"
    },
    "& .MuiSlider-thumb": {
        height: 14,
        width: 14,
        backgroundColor: "currentColor",
        border: "2px solid currentColor",
        "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
            boxShadow: "none"
        },
        "&:before": {
            display: "none"
        }
    }
}));

interface SingleValueSliderProps extends MuiSliderProps {
    value: number | undefined;
    setValue: (val: number) => void;
    hideStartMark?: boolean;
    hideEndMark?: boolean;
}

export function Slider({
    value,
    setValue,
    sx,
    valueLabelDisplay,
    hideStartMark,
    hideEndMark,
    ...props
}: SingleValueSliderProps) {
    const { textSecondary } = useAppPalette();
    return (
        <SliderBase
            sx={{
                ".MuiSlider-markLabel[data-index='0']": {
                    transform: "translateX(0)",
                    display: hideStartMark ? "none" : "block"
                },
                [`.MuiSlider-markLabel[data-index='${Array.isArray(props.marks) && props.marks.length - 1}']`]: {
                    transform: "translateX(-100%)",
                    display: hideEndMark ? "none" : "block"
                },
                ".MuiSlider-mark": { display: "none" },
                ".MuiSlider-markLabel": {
                    color: textSecondary
                },
                ...sx
            }}
            value={value ?? 0}
            onChange={(_, val) => {
                if (typeof val !== "number") return;
                setValue(val);
            }}
            valueLabelDisplay={valueLabelDisplay}
            {...props}
        />
    );
}

export function InputWrapper({
    children,
    sx,
    height = INPUT_HEIGHT,
    ...props
}: { children: ReactNode; sx?: SxType; height?: number } & Pick<StackProps, "onClick">) {
    const { border } = useAppPalette();
    return (
        <Row
            sx={{
                border,
                borderRadius: BORDER_RADIUS,
                height: height + "px",
                maxHeight: height + "px",
                minHeight: height + "px",
                px: 1,
                ...sx
            }}
            {...props}
        >
            {children}
        </Row>
    );
}

export function BackCta({
    back,
    backCta = "Back",
    textVariant = "body1"
}: {
    back: () => void;
    backCta?: string;
    textVariant?: TextVariant;
}) {
    return (
        <TextButton variant={textVariant} underlineLink={false} color="caption" onClick={back}>
            <Icon type="back" /> {backCta}
        </TextButton>
    );
}

type AutocompleteProps<T> = {
    sx?: SxType;
    options: T[] | undefined;
    value: T | undefined;
    setValue: (t: T | undefined) => void;
    renderOption?: (t: T) => ReactNode;
    getOptionLabel?: (t: T) => string;
    placeholder?: string;
    InputProps?: TextFieldProps["InputProps"];
    disableClearable?: boolean;
};
const ICON_SX: SxType = { color: (theme) => theme.palette.text.disabled, fontSize: FONT_SIZES.h3 };
export function Autocomplete<T extends string>({
    sx,
    options,
    value,
    setValue,
    renderOption,
    getOptionLabel,
    placeholder,
    InputProps,
    disableClearable
}: AutocompleteProps<T>) {
    return (
        <MuiAutocomplete
            value={value ?? null}
            onChange={(_, val) => {
                setValue(val ?? undefined);
            }}
            getOptionLabel={getOptionLabel}
            loading={options === undefined}
            options={options ?? []}
            sx={{
                ...sx,
                "& .MuiOutlinedInput-root": {
                    padding: 0, // Remove the default padding override
                    px: 2
                }
            }}
            disableClearable={disableClearable}
            clearIcon={<Close sx={ICON_SX} />}
            popupIcon={<ExpandMore sx={ICON_SX} />}
            renderOption={
                renderOption
                    ? (props, option) => {
                          return (
                              <Box component="li" {...props}>
                                  {renderOption(option)}
                              </Box>
                          );
                      }
                    : undefined
            }
            renderInput={({ InputProps: paramsInputProps, ...params }) => (
                <Input
                    InputProps={{ ...paramsInputProps, ...InputProps }}
                    placeholder={placeholder ?? "Search"}
                    {...params}
                />
            )}
        />
    );
}

const OPTION_SELECT_ROW_HEIGHT = 40;

// Rows of buttons that are used to select an option from a list.
export function OptionSelectList<T>({
    label,
    loading,
    skeletonCount,
    options,
    getKey,
    isSelected,
    isDisabled,
    onSelect,
    children,
    sx
}: {
    label?: string | undefined;
    loading: boolean;
    skeletonCount: number;
    options: T[];
    getKey: (option: T) => string;
    isSelected: (option: T) => boolean;
    isDisabled: (option: T) => boolean;
    onSelect: (option: T) => void;
    children: (option: T) => ReactNode;
    sx?: SxProps<Theme>;
}) {
    const { inputBorder, hoverOpacity } = useAppPalette();
    const getColor = useTextColorMap();
    const baseColor = getColor("primary");

    const outlinedRowSx: SxProps<Theme> = {
        width: "100%",
        minHeight: OPTION_SELECT_ROW_HEIGHT,
        maxHeight: OPTION_SELECT_ROW_HEIGHT,
        px: 1.5,
        mx: 0,
        my: 0,
        border: "1px solid",
        borderColor: inputBorder,
        borderRadius: BORDER_RADIUS,
        "&:hover": { backgroundColor: colorToAlpha(baseColor, hoverOpacity) }
    };

    return (
        <Column spacing={1}>
            {label && (
                <Text variant="body2" color="caption">
                    {label}
                </Text>
            )}
            {loading ? (
                Array(skeletonCount)
                    .fill(null)
                    .map((_, i) => (
                        <ButtonBase
                            key={i}
                            disabled
                            sx={{
                                ...outlinedRowSx,
                                ...sx,
                                mb: 1,
                                opacity: 0.5,
                                cursor: "not-allowed"
                            }}
                        >
                            <Row spaceBetween sx={{ width: "100%", height: "30px" }}>
                                <TextSkeleton variant="body2" width="80px" />
                                <TextSkeleton variant="body2" width="40px" />
                            </Row>
                        </ButtonBase>
                    ))
            ) : (
                <Column spacing={1}>
                    {options.map((option) => {
                        const selected = isSelected(option);
                        const disabled = isDisabled(option);

                        return (
                            <ButtonBase
                                key={getKey(option)}
                                disabled={disabled}
                                onClick={() => {
                                    if (disabled) return;
                                    onSelect(option);
                                }}
                                sx={{
                                    ...outlinedRowSx,
                                    ...sx,
                                    ...(selected && {
                                        borderColor: "primary.main",
                                        backgroundColor: colorToAlpha(baseColor, 0.05)
                                    }),
                                    ...(disabled && { opacity: 0.5, cursor: "not-allowed" })
                                }}
                            >
                                {children(option)}
                            </ButtonBase>
                        );
                    })}
                </Column>
            )}
        </Column>
    );
}
